/**
 * Nutrition Service — Client-side
 *
 * Calls FatSecret Cloud Function proxies for food search/lookup,
 * manages local Dexie storage for offline-first food logging.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import {
  saveNutritionEntry,
  getNutritionEntries,
  deleteNutritionEntry as dexieDelete,
  updateNutritionEntry as dexieUpdate,
  getNutritionGoals as dexieGetGoals,
  setNutritionGoals as dexieSetGoals,
  getRecentFoods,
  saveCustomMeal,
  getCustomMeals,
  deleteCustomMeal,
  getLocalDateString,
} from './db';
import type {
  NutritionEntry,
  NutritionGoals,
  DailyNutritionSummary,
  DailyNutritionGoalStatus,
  WeeklyNutritionSummary,
  NutritionAnalysisResult,
  GoalHitStatus,
  MealType,
  FatSecretFood,
  FatSecretRecipe,
  ParsedFood,
  CustomMeal,
  CustomMealItem,
} from './types/nutrition';
import { DEFAULT_NUTRITION_GOALS } from './types/nutrition';

// ============================================================================
// HELPERS
// ============================================================================

function generateId(): string {
  return `nut_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================================================
// FOOD DETAILS CACHE — avoids re-fetching the same food on every selection
// ============================================================================

const FOOD_DETAIL_CACHE = new Map<string, { data: FatSecretFood; ts: number }>();
const FOOD_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function getCachedFood(foodId: string): FatSecretFood | null {
  const entry = FOOD_DETAIL_CACHE.get(foodId);
  if (!entry) return null;
  if (Date.now() - entry.ts > FOOD_CACHE_TTL) {
    FOOD_DETAIL_CACHE.delete(foodId);
    return null;
  }
  return entry.data;
}

function cacheFood(foodId: string, data: FatSecretFood): void {
  // Cap cache at 100 entries to avoid memory bloat
  if (FOOD_DETAIL_CACHE.size > 100) {
    const oldest = FOOD_DETAIL_CACHE.keys().next().value;
    if (oldest) FOOD_DETAIL_CACHE.delete(oldest);
  }
  FOOD_DETAIL_CACHE.set(foodId, { data, ts: Date.now() });
}

// Search result cache — deduplicates rapid re-searches of the same query
const SEARCH_CACHE = new Map<string, { data: any; ts: number }>();
const SEARCH_CACHE_TTL = 60 * 1000; // 1 minute

/**
 * Search foods by query string (Premier v5 — includes images, allergens, dietary prefs)
 */
export async function searchFoods(
  query: string,
  page = 0,
  maxResults = 20,
  foodType?: 'Generic' | 'Brand'
): Promise<{ foods: any[]; totalResults: number; page: number }> {
  // Check search cache for identical recent query
  const cacheKey = `${query}|${page}|${maxResults}|${foodType || ''}`;
  const cached = SEARCH_CACHE.get(cacheKey);
  if (cached && Date.now() - cached.ts < SEARCH_CACHE_TTL) {
    return cached.data;
  }

  const fn = httpsCallable(functions, 'fatSecretSearchFoods');
  const result = await fn({ query, page, maxResults, foodType });
  const data = result.data as { foods: any[]; totalResults: number; page: number };

  // Cache the result
  if (SEARCH_CACHE.size > 20) {
    const oldest = SEARCH_CACHE.keys().next().value;
    if (oldest) SEARCH_CACHE.delete(oldest);
  }
  SEARCH_CACHE.set(cacheKey, { data, ts: Date.now() });

  return data;
}

/**
 * Autocomplete food search (Premier — type-ahead suggestions)
 */
export async function autocompleteFoods(expression: string): Promise<string[]> {
  if (expression.trim().length < 1) return [];
  const fn = httpsCallable(functions, 'fatSecretAutocompleteFoods');
  const result = await fn({ expression, maxResults: 6 });
  return (result.data as { suggestions: string[] }).suggestions;
}

/**
 * Get full food details including all servings (Premier: images, allergens, default serving)
 */
export async function getFoodDetails(foodId: string): Promise<FatSecretFood> {
  // Return cached details if available (avoids re-fetching on repeat selection)
  const cached = getCachedFood(foodId);
  if (cached) return cached;

  const fn = httpsCallable(functions, 'fatSecretGetFood');
  const result = await fn({ foodId });
  const data = result.data as { food: any };

  const raw = data.food;
  const food = normalizeFatSecretFood(raw);

  // Cache for future selections
  cacheFood(foodId, food);

  return food;
}

/** Normalize raw API response into FatSecretFood type */
function normalizeFatSecretFood(raw: any): FatSecretFood {
  // Extract food images
  const rawImages = raw.food_images?.food_image;
  const food_images = rawImages
    ? (Array.isArray(rawImages) ? rawImages : [rawImages]).map((img: any) => ({
        image_url: img.image_url,
        image_type: img.image_type || 'Standard',
      }))
    : undefined;

  // Extract allergens from food_attributes
  const attrs = raw.food_attributes?.allergens?.allergen;
  let allergens: any = undefined;
  if (attrs) {
    const list = Array.isArray(attrs) ? attrs : [attrs];
    allergens: {
      allergens = {};
      for (const a of list) {
        const key = `is_${a.name?.toLowerCase()?.replace(/\s+/g, '_')}`;
        allergens[key] = parseInt(a.value) ?? -1;
      }
    }
  }

  // Extract dietary preferences from food_attributes
  const prefs = raw.food_attributes?.preferences?.preference;
  let dietary_preferences: any = undefined;
  if (prefs) {
    const list = Array.isArray(prefs) ? prefs : [prefs];
    dietary_preferences = {};
    for (const p of list) {
      const key = `is_${p.name?.toLowerCase()?.replace(/\s+/g, '_')}`;
      dietary_preferences[key] = parseInt(p.value) ?? -1;
    }
  }

  return {
    food_id: raw.food_id,
    food_name: raw.food_name,
    brand_name: raw.brand_name,
    food_type: raw.food_type,
    food_url: raw.food_url,
    servings: raw.servings?.serving || [],
    food_images,
    allergens,
    dietary_preferences,
  };
}

/**
 * Search food by barcode (UPC/EAN) — Premier v2 (full food in one API call)
 */
export async function searchByBarcode(barcode: string): Promise<FatSecretFood> {
  const fn = httpsCallable(functions, 'fatSecretSearchByBarcode');
  const result = await fn({ barcode });
  const data = result.data as { food: any };
  return normalizeFatSecretFood(data.food);
}

/**
 * Search recipes by query string
 */
export async function searchRecipes(
  query: string,
  page = 0,
  maxResults = 20,
  filters?: { maxCalories?: number; minProteinPct?: number; maxPrepTime?: number }
): Promise<{ recipes: FatSecretRecipe[]; totalResults: number; page: number }> {
  const fn = httpsCallable(functions, 'fatSecretSearchRecipes');
  const result = await fn({ query, page, maxResults, ...filters });
  return result.data as { recipes: FatSecretRecipe[]; totalResults: number; page: number };
}

/**
 * Parse natural language food description into matched foods with macros
 * e.g. "a bowl of oatmeal with banana and honey"
 */
export async function parseNaturalLanguage(
  text: string,
  region?: string,
  language?: string
): Promise<ParsedFood[]> {
  const fn = httpsCallable(functions, 'fatSecretNaturalLanguage');
  const result = await fn({
    foodDescription: text,
    region: region || 'US',
    language: language || 'en',
    includeFoodData: true,
  });
  const data = result.data as { parsedFoods: ParsedFood[] };
  return data.parsedFoods || [];
}

/**
 * Recognize foods from a photo (base64-encoded image)
 * Returns recognized foods with macros and confidence
 */
export async function recognizeImage(
  imageBase64: string,
  region?: string,
  language?: string
): Promise<ParsedFood[]> {
  const fn = httpsCallable(functions, 'fatSecretImageRecognition', { timeout: 60000 });
  const result = await fn({
    imageBase64,
    region: region || 'US',
    language: language || 'en',
    includeFoodData: true,
  });
  const data = result.data as { recognizedFoods: ParsedFood[] };
  return data.recognizedFoods || [];
}

// ============================================================================
// LOCAL FOOD LOGGING (Dexie)
// ============================================================================

/**
 * Log a food entry to Dexie
 */
export async function logFood(
  entry: Omit<NutritionEntry, 'id' | 'createdAt' | 'updatedAt' | 'syncStatus'>
): Promise<NutritionEntry> {
  const full: NutritionEntry = {
    ...entry,
    id: generateId(),
    syncStatus: 'local',
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  await saveNutritionEntry(full);
  return full;
}

/**
 * Delete a food entry
 */
export async function deleteFood(id: string): Promise<void> {
  await dexieDelete(id);
}

/**
 * Update a food entry (e.g. change serving size)
 */
export async function updateFood(
  id: string,
  updates: Partial<NutritionEntry>
): Promise<void> {
  await dexieUpdate(id, updates);
}

/**
 * Get daily nutrition summary with totals and goals
 */
export async function getDailySummary(
  userId: string,
  date: string
): Promise<DailyNutritionSummary> {
  const entries = await getNutritionEntries(userId, date);
  const goals = await dexieGetGoals(userId);

  const totals = entries.reduce(
    (acc, e) => ({
      calories: acc.calories + e.calories,
      protein: acc.protein + e.protein,
      carbs: acc.carbs + e.carbs,
      fat: acc.fat + e.fat,
      fiber: acc.fiber + (e.fiber || 0),
      sugar: acc.sugar + (e.sugar || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 }
  );

  return {
    date,
    totalCalories: Math.round(totals.calories),
    totalProtein: Math.round(totals.protein),
    totalCarbs: Math.round(totals.carbs),
    totalFat: Math.round(totals.fat),
    totalFiber: Math.round(totals.fiber),
    totalSugar: Math.round(totals.sugar),
    entries,
    goalCalories: goals.calories,
    goalProtein: goals.protein,
    goalCarbs: goals.carbs,
    goalFat: goals.fat,
    goalFiber: goals.fiber ?? 30,
    goalSugar: goals.sugar ?? 50,
  };
}

/**
 * Get nutrition goals for a user
 */
export async function getNutritionGoals(userId: string): Promise<NutritionGoals> {
  return dexieGetGoals(userId);
}

/**
 * Set nutrition goals for a user
 */
export async function setNutritionGoals(
  userId: string,
  goals: NutritionGoals
): Promise<void> {
  return dexieSetGoals(userId, goals);
}

/**
 * Get recent unique foods for quick re-logging
 */
export async function getRecentFoodsList(
  userId: string,
  limit = 20
): Promise<NutritionEntry[]> {
  return getRecentFoods(userId, limit);
}

// ============================================================================
// CUSTOM MEALS (Saved Recipes)
// ============================================================================

/**
 * Create and save a custom meal from a list of ingredients
 */
export async function createCustomMeal(
  userId: string,
  name: string,
  items: CustomMealItem[]
): Promise<CustomMeal> {
  const totals = items.reduce(
    (acc, item) => ({
      calories: acc.calories + item.calories,
      protein: acc.protein + item.protein,
      carbs: acc.carbs + item.carbs,
      fat: acc.fat + item.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const meal: CustomMeal = {
    id: `meal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    userId,
    name,
    items,
    totalCalories: Math.round(totals.calories),
    totalProtein: Math.round(totals.protein),
    totalCarbs: Math.round(totals.carbs),
    totalFat: Math.round(totals.fat),
    syncStatus: 'local',
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await saveCustomMeal(meal);
  return meal;
}

/**
 * Get all saved custom meals for a user
 */
export async function getCustomMealsList(userId: string): Promise<CustomMeal[]> {
  return getCustomMeals(userId);
}

/**
 * Delete a custom meal by ID
 */
export async function deleteCustomMealById(id: string): Promise<void> {
  return deleteCustomMeal(id);
}

/**
 * Log a custom meal as a single nutrition entry
 */
export async function logCustomMeal(
  userId: string,
  date: string,
  mealType: MealType,
  customMeal: CustomMeal
): Promise<NutritionEntry> {
  return logFood({
    userId,
    date,
    mealType,
    foodName: customMeal.name,
    servingSize: 1,
    servingUnit: 'meal',
    calories: customMeal.totalCalories,
    protein: customMeal.totalProtein,
    carbs: customMeal.totalCarbs,
    fat: customMeal.totalFat,
    isQuickAdd: true,
  });
}

// ============================================================================
// GOAL TRACKING & ANALYSIS
// ============================================================================

/**
 * Body composition goal — drives whether the calorie target is treated as
 * a CEILING (cut), FLOOR (gain), or band (maintain/recomp). Without this,
 * a user on a cut who eats 300 cal under target was being graded as
 * "missed" — completely backwards.
 */
export type DietDirection = 'lose' | 'maintain' | 'gain';

function classifyGoalHit(actual: number, goal: number): GoalHitStatus {
  if (goal <= 0) return 'hit';
  const ratio = actual / goal;
  if (ratio >= 0.9 && ratio <= 1.1) return 'hit';
  if (ratio > 1.1) return 'over';
  return 'under';
}

/**
 * Calorie-specific classification that respects the user's body-comp goal.
 *   - lose:     under-target OR at-target = 'hit', over = 'over' (the bad one)
 *   - maintain: ±10% band = 'hit' (same as default classifyGoalHit)
 *   - gain:     over-target OR at-target = 'hit', under = 'under' (the bad one)
 *
 * 'hit' always means "you're on track for your goal." The UI uses this
 * to paint green/red appropriately.
 */
function classifyCaloriesByGoal(
  actual: number,
  goal: number,
  direction: DietDirection,
): GoalHitStatus {
  if (goal <= 0) return 'hit';
  const ratio = actual / goal;
  switch (direction) {
    case 'lose':
      // Under = good, slightly over (≤10%) = still tolerable
      if (ratio <= 1.0) return 'hit';
      if (ratio <= 1.1) return 'hit';
      return 'over';
    case 'gain':
      // Over = good, slightly under (≥90%) = still tolerable
      if (ratio >= 1.0) return 'hit';
      if (ratio >= 0.9) return 'hit';
      return 'under';
    case 'maintain':
    default:
      return classifyGoalHit(actual, goal);
  }
}

/**
 * Compute goal status for a daily summary (hit/miss per macro + allGoalsHit)
 */
export function computeGoalStatus(
  summary: DailyNutritionSummary,
  dietDirection: DietDirection = 'maintain',
): DailyNutritionGoalStatus {
  // Calories are interpreted relative to the user's body-comp goal:
  // under-target reads as "on track" for a cut, "missing" for a gain.
  // Macros (protein / carbs / fat) keep the ±10% band — they're
  // composition targets independent of energy balance.
  const calStatus = classifyCaloriesByGoal(summary.totalCalories, summary.goalCalories, dietDirection);
  const proStatus = classifyGoalHit(summary.totalProtein, summary.goalProtein);
  const carbStatus = classifyGoalHit(summary.totalCarbs, summary.goalCarbs);
  const fatStatus = classifyGoalHit(summary.totalFat, summary.goalFat);
  // Fiber/sugar: auto-hit if no goal set (goalFiber/goalSugar = 0)
  const fiberStatus = summary.goalFiber > 0 ? classifyGoalHit(summary.totalFiber, summary.goalFiber) : 'hit' as GoalHitStatus;
  const sugarStatus = summary.goalSugar > 0 ? classifyGoalHit(summary.totalSugar, summary.goalSugar) : 'hit' as GoalHitStatus;

  return {
    date: summary.date,
    caloriesStatus: calStatus,
    proteinStatus: proStatus,
    carbsStatus: carbStatus,
    fatStatus: fatStatus,
    fiberStatus,
    sugarStatus,
    allGoalsHit: calStatus === 'hit' && proStatus === 'hit' && carbStatus === 'hit' && fatStatus === 'hit',
    deltas: {
      calories: summary.totalCalories - summary.goalCalories,
      protein: summary.totalProtein - summary.goalProtein,
      carbs: summary.totalCarbs - summary.goalCarbs,
      fat: summary.totalFat - summary.goalFat,
      fiber: summary.totalFiber - summary.goalFiber,
      sugar: summary.totalSugar - summary.goalSugar,
    },
    calorieAdherencePct: summary.goalCalories > 0
      ? Math.round((summary.totalCalories / summary.goalCalories) * 100)
      : 0,
  };
}

/**
 * Get weekly nutrition summaries for a date range
 * startDate should be a Monday (YYYY-MM-DD)
 */
export async function getWeekNutritionSummary(
  userId: string,
  startDate: string,
  dietDirection: DietDirection = 'maintain',
): Promise<WeeklyNutritionSummary> {
  const start = new Date(startDate + 'T12:00:00');
  const days: (DailyNutritionGoalStatus | null)[] = [];

  let daysTracked = 0;
  let daysCaloriesHit = 0;
  let daysProteinHit = 0;
  let daysAllGoalsHit = 0;
  let totalAdherence = 0;

  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    // Match the LOCAL date key used when entries are saved.
    const dateStr = getLocalDateString(d);

    try {
      const summary = await getDailySummary(userId, dateStr);
      if (summary.entries.length === 0) {
        days.push(null);
        continue;
      }
      const status = computeGoalStatus(summary, dietDirection);
      days.push(status);
      daysTracked++;
      totalAdherence += status.calorieAdherencePct;
      if (status.caloriesStatus === 'hit') daysCaloriesHit++;
      if (status.proteinStatus === 'hit') daysProteinHit++;
      if (status.allGoalsHit) daysAllGoalsHit++;
    } catch {
      days.push(null);
    }
  }

  const endD = new Date(start);
  endD.setDate(start.getDate() + 6);

  return {
    startDate,
    endDate: getLocalDateString(endD),
    days,
    daysTracked,
    daysCaloriesHit,
    daysProteinHit,
    daysAllGoalsHit,
    avgCalorieAdherence: daysTracked > 0 ? Math.round(totalAdherence / daysTracked) : 0,
  };
}

/**
 * Get nutrition analysis for the intelligence engine
 * Analyzes the last N days of nutrition data
 */
export async function getNutritionAnalysis(
  userId: string,
  days = 7,
  dietDirection: DietDirection = 'maintain',
): Promise<NutritionAnalysisResult | null> {
  const today = new Date();
  let daysTracked = 0;
  let totalCalorieAdherence = 0;
  let totalProtein = 0;
  let totalCarbs = 0;
  let totalFat = 0;
  let totalCalorieBalance = 0;
  let streak = 0;
  let streakBroken = false;

  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    // Nutrition entries are keyed by LOCAL date (getLocalDateString on
    // save). Using toISOString here keyed by UTC, which in any behind-UTC
    // timezone shifts the window by a day — so logged days fell outside
    // the query and daysTracked got stuck low.
    const dateStr = getLocalDateString(d);

    try {
      const summary = await getDailySummary(userId, dateStr);
      if (summary.entries.length === 0) {
        if (!streakBroken) streakBroken = true;
        continue;
      }

      const status = computeGoalStatus(summary, dietDirection);
      daysTracked++;
      totalCalorieAdherence += status.calorieAdherencePct;
      totalProtein += summary.totalProtein;
      totalCarbs += summary.totalCarbs;
      totalFat += summary.totalFat;
      totalCalorieBalance += status.deltas.calories;

      if (!streakBroken && status.allGoalsHit) {
        streak++;
      } else if (!streakBroken) {
        streakBroken = true;
      }
    } catch {
      if (!streakBroken) streakBroken = true;
    }
  }

  if (daysTracked === 0) return null;

  // Try to get bodyweight for protein per lb calculation
  let proteinPerLb: number | null = null;
  try {
    const { getTodayActivity } = await import('./db');
    const activity = await getTodayActivity(userId);
    const weightKg = (activity as any)?.weight;
    if (weightKg && weightKg > 0) {
      const weightLbs = weightKg * 2.20462;
      proteinPerLb = Math.round(((totalProtein / daysTracked) / weightLbs) * 100) / 100;
    }
  } catch { /* non-critical */ }

  return {
    avgCalorieAdherence: Math.round(totalCalorieAdherence / daysTracked),
    avgProteinGrams: Math.round(totalProtein / daysTracked),
    proteinPerLbBodyweight: proteinPerLb,
    avgCarbGrams: Math.round(totalCarbs / daysTracked),
    avgFatGrams: Math.round(totalFat / daysTracked),
    streak,
    daysAnalyzed: days,
    daysTracked,
    avgCalorieBalance: Math.round(totalCalorieBalance / daysTracked),
  };
}
